/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
// utils
import { APIStringtoDate } from '../../../../utils/date';
import { 
    fetchTotalCommentsReport, 
    fetchTotalCommentingViewsReport,
    fetchCommentingUsersReport, 
    fetchTotalCommentingReactionsReport, 
    fetchTopArticlesReport,
    fetchCommentingBeelineReport,
    fetchEmailsSentReport,
    fetchTopArticlesExportAll,
    fetchAvgInViewReport
} from '../../../../state/thunks/commenting';
import { fetchProductDataLastUpdated } from '../../../../state/thunks/product';
import { getDevices } from '../../../../utils/filterhelpers';
// material
import { Page, Content, InContentMessage} from '@insticator/insticator-ui';
import Header from '../../../../material/structure/header/header';
import PageHeader from '../../../../material/structure/pageheader/pageheader';
import PageContent from '../../../../material/structure/pagecontent/pagecontent';
import GlobalFilter from '../../../../material/globalfilter/globalfilter';
// styles
import injectSheet from 'react-jss';
import styles from './commentinganalytics_styles';
import TotalCommentsReport from "./totalcommentsreport/totalcommentsreport";
import TopArticlesReport from "./toparticlesreport/toparticlesreport";
import CommentingUsersReport from './commentingusersreport/commentingusersreport';
import TotalCommentingReactionsReport from './totalcommentingreactionsreport/totalcommentingreactionsreport';
import CommentingBeelineReport from './commentingbeelinereport/commentingbeelinereport';
import EmailsSentReport from './emailssentreport/emailssentreport';
import AverageInViewReport from './avginviewreport/avginviewreport';




/* ========== ~~~~~~~~~~ COMMENTING - ANALYTICS PAGE ~~~~~~~~~~ ========== */

export default injectSheet(styles)((props) => {
    // state
    const { accountUUID } = useSelector((state) => state.session);
    const { sites, dateRange, devices, compareTo, countries } = useSelector((state) => state.app.filters);
    const { lastUpdated } = useSelector(state => state.commenting);
    const [selectedInterval, setSelectedInterval] = useState('daily');

    // actions
    const dispatch = useDispatch();

    // request reports on division load
    useEffect(() => {
        sendReportRequests()
    }, [sites, devices, compareTo, countries, selectedInterval]);

    const sendReportRequests = () => {
        getAvgInViewReport()
        getTotalCommentsReport()
        getTotalCommentingViewsReport()
        getCommentingUsersReport()
        getTotalCommentingReactionsReport()
        getTopArticlesReport()
        getCommentingBeelineReport()
        getEmailsSentReport()
        getProductDataLastUpdated()
    }

    const getAvgInViewReport = () => dispatch(fetchAvgInViewReport({requestData: getRequestData()}))
    const getProductDataLastUpdated = () => dispatch(fetchProductDataLastUpdated('COMMENTING'));
    const getTotalCommentsReport = () => dispatch(fetchTotalCommentsReport({requestData: { ...getRequestData()}}));
    const getTotalCommentingViewsReport = () => dispatch(fetchTotalCommentingViewsReport({requestData: { ...getRequestData(), devices: [], countries: []}}));
    const getCommentingUsersReport = () => dispatch(fetchCommentingUsersReport({requestData: { ...getRequestData()}}));
    const getTotalCommentingReactionsReport = () => dispatch(fetchTotalCommentingReactionsReport({requestData: { ...getRequestData()}}));
    const getTopArticlesReport = () => dispatch(fetchTopArticlesReport({requestData: getRequestData()}));
    const getCommentingBeelineReport = () => dispatch(fetchCommentingBeelineReport({requestData: { ...getRequestData()}}));
    const getEmailsSentReport = () => dispatch(fetchEmailsSentReport({requestData: getRequestData()}))

    const handleTopArticleExportAllClick = () => dispatch(fetchTopArticlesExportAll(getRequestData()));

    const getRequestData = () => ({
        accountUUID,
        sites: sites,
        startDate: dateRange.from,
        endDate: dateRange.to,
        granularity: selectedInterval.toUpperCase(),
        devices: getDevices(devices, 'commenting'),
        comparisonType: compareTo,
        countries: countries.length === 250 ? [] : countries
    });

    const getBannerText = useCallback(() => {
        // if start date is before June 21st 2021
        const june21stDate = new Date(2021, 5, 21)
         if (dateRange.from && APIStringtoDate(dateRange.from) < june21stDate){
             return 'There is no commenting data prior to June 21st, 2021';
         }
         return null;
    }, [dateRange]);

    return (
        <Page name={'commentinganalyticspage'} klass={`${props.classes.commentinganalyticspage}`} >
            <Header>
                <GlobalFilter onFilterApply={sendReportRequests}/>
            </Header>
            <Content>
                <InContentMessage/>
                <PageHeader
                    lastUpdatedTimestamp={lastUpdated}
                    title={'Commenting'}
                    subtitle={'Analytics'}
                    dataTip={true}
                    bannerText={getBannerText()} />
                <PageContent>
                    <AverageInViewReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <TotalCommentsReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <CommentingUsersReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    <TotalCommentingReactionsReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                    {/* <GuestCommentingInteractionsReport/>  TODO: Hide this for now since we don't have the data/API yet */}
                    <TopArticlesReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} onExportAllClick={handleTopArticleExportAllClick}/>
                    <CommentingBeelineReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval}/>
                    <EmailsSentReport selectedInterval={selectedInterval} setSelectedInterval={setSelectedInterval} />
                </PageContent>
            </Content>
        </Page>
    );
});
